import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { BRAND_BLUE } from './theme/appTheme';
import { AuthProvider, useAuth } from './authContext/authContext';
import { PortalProvider } from './authContext/portalContext';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import Login from './pages/auth/Login';
import Orders from './pages/admin/Orders';
import MainLayout from './components/MainLayout';
import Analysis from './pages/admin/Analysis';
import AnalysisWorkspace from './pages/admin/analysisWorkspace/AnalysisWorkspace';
import Review from './pages/admin/Review';
import Therapist from './pages/admin/Therapist';
import Operators from './pages/admin/Operators';
import PortalUsers from './pages/admin/PortalUsers';
import CustomerDetail from './pages/admin/CustomerDetail';
import OrderDetail from './pages/admin/OrderDetail';
import Form from './pages/form'
import BarcodeScanner from './pages/admin/BarcodeScanner';
import OCR from './pages/admin/OCR';
import ProtectedRoute from "./components/protectedRoute";
import PublicOnlyRoute from "./components/PublicOnlyRoute";
import Usage from "./pages/admin/Usage";
import UsageRequestDetail from "./pages/admin/usage/RequestDetail";
import PortalRoute from "./components/PortalRoute";
import PatientOrders from "./pages/patient/PatientOrders";
import PatientReports from "./pages/patient/PatientReports";
import TherapistPatients from "./pages/therapist/TherapistPatients";
import TherapistPatientOrders from "./pages/therapist/TherapistPatientOrders";
import TherapistReports from "./pages/therapist/TherapistReports";
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import DashboardRouter from './pages/dashboard/DashboardRouter';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useMaintenanceMode } from './Services/maintenanceMode';
import MaintenancePage from './components/MaintenancePage';
import UpdateBanner from './components/UpdateBanner';

function AddDataRedirect() {
  const { orderId, testId } = useParams();
  return <Navigate to={`/analysis/${orderId}/${testId}?tab=add-data`} replace />;
}

function ReportRedirect() {
  const { orderId, testId } = useParams();
  return <Navigate to={`/analysis/${orderId}/${testId}?tab=report`} replace />;
}

// Auto-applies the logged-in account's stored language preference as the
// portal's active display language — on fresh login, on page-load session
// bootstrap, and after a saved language change (GeneralSettings/switcher
// both funnel through authContext's `user` state, so a single effect here
// covers all three). Renders nothing; must sit inside both LanguageProvider
// and AuthProvider.
function LanguageSync() {
  const { user } = useAuth();
  const { setLanguage } = useLanguage();

  useEffect(() => {
    if (user?.languageCode) setLanguage(user.languageCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return null;
}

function App() {
  const maintenanceStatus = useMaintenanceMode(); // "checking" | "maintenance" | "normal"

  if (maintenanceStatus === "maintenance") {
    return (
      <LanguageProvider>
        <MaintenancePage />
      </LanguageProvider>
    );
  }

  if (maintenanceStatus === "checking") {
    // Blocks routing/auth/API calls until the maintenance flag check
    // resolves - see src/Services/maintenanceMode.js. Deliberately blank
    // (not the maintenance message) since being "off" is the common path.
    return (
      <Box sx={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress size={32} sx={{ color: BRAND_BLUE }} />
      </Box>
    );
  }

  return (
    <LanguageProvider>
    <AuthProvider>
      <LanguageSync />
      <PortalProvider>
      <ToastContainer />
      <UpdateBanner />
      <Routes>
        {/* Default route - dashboard if a session is stored in this browser, otherwise login */}
        <Route path="/" element={<PublicOnlyRoute><Navigate to="/login" replace /></PublicOnlyRoute>} />
        <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />

        {/* Registration and password reset (emailed code) - public, no auth required */}
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Pages the old emailed links opened - codes are now in the email itself */}
        <Route path="/login/verify" element={<Navigate to="/login" replace />} />
        <Route path="/register/verify" element={<Navigate to="/register" replace />} />
        <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />

        {/* Public form routes - no authentication required */}
        <Route path="/form" element={<Form/>}/>
        <Route path="/managePatientForms/form/:labTestId" element={<Form/>}/>

        {/* Reports now live inside the portal - old standalone verify/report links bounce to login */}
        <Route path="/verify/:id" element={<Navigate to="/login" replace />} />
        <Route path="/therapist-report/:labTestId" element={<Navigate to="/login" replace />} />

        {/* Protected Routes (With Sidebar) - each page is only reachable from its own portal (see PortalRoute) */}
        <Route element={<MainLayout />}>
          <Route path="/dashboard" element={ <ProtectedRoute> <DashboardRouter /></ProtectedRoute> }/>

          {/* Back office (admin + operator) */}
          <Route path="/orders"    element={ <ProtectedRoute> <PortalRoute portal="admin"> <Orders /> </PortalRoute> </ProtectedRoute> }/>
          <Route path="/orders/:orderId" element={ <ProtectedRoute> <PortalRoute portal="admin"> <OrderDetail /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/analysis" element={ <ProtectedRoute> <PortalRoute portal="admin"> <Analysis /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/analysis/:orderId/:testId" element={ <ProtectedRoute> <PortalRoute portal="admin"> <AnalysisWorkspace /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/analysis/:orderId/:testId/add-data" element={ <ProtectedRoute> <PortalRoute portal="admin"> <AddDataRedirect /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/analysis/:orderId/:testId/report" element={ <ProtectedRoute> <PortalRoute portal="admin"> <ReportRedirect /> </PortalRoute> </ProtectedRoute> } />

          {/* Back office, admin role only */}
          <Route path="/review" element={ <ProtectedRoute> <PortalRoute portal="admin" adminOnly> <Review /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/therapist" element={ <ProtectedRoute> <PortalRoute portal="admin" adminOnly> <Therapist /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/operators" element={ <ProtectedRoute> <PortalRoute portal="admin" adminOnly> <Operators /> </PortalRoute> </ProtectedRoute> }  />
          <Route path="/portal-users" element={ <ProtectedRoute> <PortalRoute portal="admin" adminOnly> <PortalUsers /> </PortalRoute> </ProtectedRoute> }  />
          <Route path="/usage" element={ <ProtectedRoute> <PortalRoute portal="admin" adminOnly> <Usage /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/usage/requests/:id" element={ <ProtectedRoute> <PortalRoute portal="admin" adminOnly> <UsageRequestDetail /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/portal-users/customers/:customerId" element={ <ProtectedRoute> <PortalRoute portal="admin" adminOnly> <CustomerDetail /> </PortalRoute> </ProtectedRoute> } />

          {/* Patient portal */}
          <Route path="/patient/orders" element={ <ProtectedRoute> <PortalRoute portal="patient"> <PatientOrders /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/patient/reports" element={ <ProtectedRoute> <PortalRoute portal="patient"> <PatientReports /> </PortalRoute> </ProtectedRoute> } />

          {/* Therapist portal */}
          <Route path="/therapist/patients" element={ <ProtectedRoute> <PortalRoute portal="therapist"> <TherapistPatients /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/therapist/patients/:patientKey" element={ <ProtectedRoute> <PortalRoute portal="therapist"> <TherapistPatientOrders /> </PortalRoute> </ProtectedRoute> } />
          <Route path="/therapist/reports" element={ <ProtectedRoute> <PortalRoute portal="therapist"> <TherapistReports /> </PortalRoute> </ProtectedRoute> } />
        </Route>

        {/* Protected back-office routes (Without Sidebar) */}
        <Route path="/qrscanner" element={<ProtectedRoute> <PortalRoute portal="admin"> <BarcodeScanner/> </PortalRoute> </ProtectedRoute>}/>
        <Route path="/ocr" element={<ProtectedRoute> <PortalRoute portal="admin"> <OCR/> </PortalRoute> </ProtectedRoute>}/>

        {/* Unknown URLs - logged-in users go to their own dashboard, others to login */}
        <Route path="*" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      </Routes>
      </PortalProvider>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;